import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LoupeIcon from '@mui/icons-material/Loupe';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import TuneIcon from '@mui/icons-material/Tune';
import BadgeIcon from '@mui/icons-material/Badge';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArticleIcon from '@mui/icons-material/Article';
import axios from 'axios';
import { ThemeContext, UserContext } from '../Context';
import { normalizeReportNumber, normalizeReportYear, splitReportInput } from '../../utils/reportNumber';
import { normalizeRubricHtml } from '../../utils/normalizeRubricHtml';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
import { getEncodagePalette as getPalette } from '../../theme/encodagePalette';

const ELEMENT_KEYS = ['intervenants', 'faits', 'personnesPhysiques', 'personnesMorales', 'vehicules', 'objets'];
const LIST_KEYS = [...ELEMENT_KEYS, 'rubrics'];

const SECTIONS = [
  { key: 'general', label: 'Informations générales', icon: TuneIcon, description: 'Remplacent les valeurs du PV ouvert.' },
  { key: 'intervenants', label: 'Intervenants', icon: BadgeIcon, description: 'Ajoutés à la suite des intervenants existants.' },
  { key: 'faits', label: 'Faits', icon: GavelIcon, description: 'Ajoutés à la suite des faits existants.' },
  { key: 'personnesPhysiques', label: 'Personnes physiques', icon: PersonIcon, description: 'Ajoutées à la suite des personnes existantes.' },
  { key: 'personnesMorales', label: 'Personnes morales', icon: BusinessIcon, description: 'Ajoutées à la suite des personnes morales existantes.' },
  { key: 'vehicules', label: 'Véhicules', icon: DirectionsCarIcon, description: 'Ajoutés à la suite des véhicules existants.' },
  { key: 'objets', label: 'Objets', icon: Inventory2Icon, description: 'Ajoutés à la suite des objets existants.' },
  { key: 'rubrics', label: 'Rubriques', icon: ArticleIcon, description: 'Copiées sans leurs commentaires.' }
];
const SECTION_KEYS = SECTIONS.map((section) => section.key);

const GENERAL_LOG_LABELS = {
  structure: 'structure',
  service: 'service',
  startDate: 'début de rédaction',
  endDate: 'date de clôture'
};

const norm = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const compact = (value) => norm(value).replace(/[^a-z0-9]/g, '');

const htmlToText = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const plural = (count, singular, pluralForm = `${singular}s`) => `${count} ${count > 1 ? pluralForm : singular}`;

const pad = (value) => String(value).padStart(2, '0');

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);
  return date ? `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}` : '';
};

const formatDateTime = (value) => {
  const date = toDate(value);
  return date ? `${formatDate(value)} à ${pad(date.getHours())}:${pad(date.getMinutes())}` : '';
};

const formatFaitHeading = (fait) => {
  if (!fait) return '';
  const introductions = [];
  if (fait.isQualified) introductions.push('Fait qualifié de');
  if (fait.isTentative) introductions.push('Tentative de');
  const noyau = fait.fait || '';
  if (!introductions.length) return noyau;

  const texte = introductions.map((introduction, index) => {
    const suivants = introductions.length - index;
    const libelle = index === 0
      ? introduction
      : `${introduction.charAt(0).toLowerCase()}${introduction.slice(1)}`;
    return suivants > 1 ? libelle : `${libelle} :`;
  }).join(' ');

  return `${texte} ${noyau}`.trim();
};

const describeStructure = (report) => {
  if (report?.reportType === 'initial') {
    const indice = report.indice && report.indice !== '__' ? report.indice : '';
    return indice ? `PV initial - indice ${indice}` : 'PV initial';
  }
  return report?.reportType === 'subsequent' ? 'PV subséquent' : 'Non défini';
};

const describeElement = {
  intervenants: (item) => ({
    primary: `${(item.lastname || '').toUpperCase()} ${item.firstname || ''}`.trim() || 'Intervenant',
    secondary: [item.rank],
    tags: [item.sign && 'Signataire', item.isOPJ && 'OPJ', item.isOPA && 'OPA', item.isExternal && 'Membre extérieur']
  }),
  faits: (item) => {
    if (item.isUndetermined) {
      return { primary: 'Fait non déterminé - procès-verbal subséquent', secondary: [], tags: [] };
    }
    const when = item.timeType === 'range'
      ? `Entre le ${formatDateTime(item.rangeLowDateTime) || '…'} et le ${formatDateTime(item.rangeHighDateTime) || '…'}`
      : (item.fixedDateTime ? `Le ${formatDateTime(item.fixedDateTime)}` : '');
    const place = `${[item.country, item.address].filter(Boolean).join(', ')}${item.destination ? ` (${item.destination})` : ''}`.trim();
    return {
      primary: formatFaitHeading(item) || 'Fait',
      secondary: [item.article && `Article : ${item.article}`, when, place && `À ${place}`],
      tags: []
    };
  },
  personnesPhysiques: (item) => {
    const born = item.gender === 'M' ? 'Né' : item.gender === 'F' ? 'Née' : 'Né(e)';
    const birth = [
      item.birthdate && `${born} le ${formatDate(item.birthdate) || item.birthdate}`,
      item.birthPlace && `${item.birthdate ? 'à' : `${born} à`} ${item.birthPlace}`
    ].filter(Boolean).join(' ');
    const name = [(item.lastname || '').toUpperCase(), item.firstname, item.otherFirstnames].filter(Boolean).join(' ');
    return {
      primary: item.isUnknown ? 'Individu inconnu' : (name || 'Personne physique'),
      secondary: [
        birth,
        item.nationalNumber && `N° national : ${item.nationalNumber}`,
        item.nationality && `Nationalité : ${item.nationality}`,
        item.residence && `Domicile : ${item.residence}`
      ],
      tags: [item.qualite, ...(item.additionalQualities || [])]
    };
  },
  personnesMorales: (item) => ({
    primary: item.denomination || 'Personne morale',
    secondary: [
      item.companyNumber && `N° d'entreprise : ${item.companyNumber}`,
      item.headquarters && `Siège social : ${item.headquarters}`
    ],
    tags: [item.qualite]
  }),
  vehicules: (item) => ({
    primary: item.brand || 'Véhicule',
    secondary: [
      item.plateNumber && `Plaque : ${item.plateNumber}${item.nationality ? ` (${item.nationality})` : ''}`,
      item.chassisNumber && `Châssis : ${item.chassisNumber}`,
      item.color && `Couleur : ${item.color}`
    ],
    tags: [item.category]
  }),
  objets: (item) => ({
    primary: item.description || 'Objet',
    secondary: [item.sinNumber && `N° SIN : ${item.sinNumber}`],
    tags: []
  })
};

// Un élément est signalé « déjà présent » quand il semble décrire la même chose qu'un élément du PV ouvert
const sameElement = {
  intervenants: (a, b) => Boolean(
    (a.userId && b.userId && a.userId === b.userId) ||
    (norm(a.lastname) === norm(b.lastname) && norm(a.firstname) === norm(b.firstname))
  ),
  faits: (a, b) => {
    if (a.isUndetermined || b.isUndetermined) return Boolean(a.isUndetermined && b.isUndetermined);
    return norm(a.fait) === norm(b.fait) &&
      (a.timeType || 'fixed') === (b.timeType || 'fixed') &&
      (a.fixedDateTime || '') === (b.fixedDateTime || '') &&
      (a.rangeLowDateTime || '') === (b.rangeLowDateTime || '') &&
      (a.rangeHighDateTime || '') === (b.rangeHighDateTime || '') &&
      norm(a.address) === norm(b.address);
  },
  personnesPhysiques: (a, b) => {
    if (compact(a.nationalNumber) && compact(b.nationalNumber)) {
      return compact(a.nationalNumber) === compact(b.nationalNumber);
    }
    if (a.isUnknown || b.isUnknown) {
      return Boolean(a.isUnknown && b.isUnknown && norm(a.qualite) === norm(b.qualite));
    }
    return norm(a.lastname) === norm(b.lastname) &&
      norm(a.firstname) === norm(b.firstname) &&
      (a.birthdate || '') === (b.birthdate || '');
  },
  personnesMorales: (a, b) => Boolean(
    (compact(a.companyNumber) && compact(a.companyNumber) === compact(b.companyNumber)) ||
    norm(a.denomination) === norm(b.denomination)
  ),
  vehicules: (a, b) => {
    if (compact(a.plateNumber) && compact(a.plateNumber) === compact(b.plateNumber)) return true;
    if (compact(a.chassisNumber) && compact(a.chassisNumber) === compact(b.chassisNumber)) return true;
    return !compact(a.plateNumber) && !compact(a.chassisNumber) &&
      norm(a.brand) === norm(b.brand) && norm(a.category) === norm(b.category);
  },
  objets: (a, b) => norm(a.description) === norm(b.description) && compact(a.sinNumber) === compact(b.sinNumber)
};

const accentTags = (labels) => labels.filter(Boolean).map((label) => ({ label, tone: 'accent' }));

const buildCandidates = (source, target) => {
  const current = target || {};

  const general = [
    source.reportType && {
      key: 'structure',
      label: 'Structure',
      value: describeStructure(source),
      current: describeStructure(current)
    },
    source.unit && {
      key: 'service',
      label: 'Service',
      value: source.unit,
      current: current.unit || 'Non défini'
    },
    source.StartDate && {
      key: 'startDate',
      label: 'Début de la rédaction',
      value: formatDateTime(source.StartDate) || source.StartDate,
      current: formatDateTime(current.StartDate) || 'Non défini'
    },
    source.EndDate && {
      key: 'endDate',
      label: 'Date de clôture',
      value: formatDate(source.EndDate) || source.EndDate,
      current: formatDate(current.EndDate) || 'Non défini'
    }
  ].filter(Boolean).map((item) => ({
    ...item,
    identical: item.value === item.current,
    searchText: norm(`${item.label} ${item.value}`)
  }));

  const candidates = { general };

  ELEMENT_KEYS.forEach((key) => {
    const existing = current[key] || [];
    candidates[key] = (source[key] || []).map((item) => {
      const view = describeElement[key](item);
      const secondary = view.secondary.filter(Boolean);
      const tags = accentTags(view.tags);
      return {
        key: String(item._id),
        primary: view.primary,
        secondary,
        tags,
        duplicate: existing.some((other) => sameElement[key](item, other)),
        searchText: norm([view.primary, ...secondary, ...tags.map((tag) => tag.label)].join(' '))
      };
    });
  });

  const existingRubrics = new Set(
    (current.rubrics || []).map((rubric) => `${norm(rubric.title)}|${norm(htmlToText(rubric.content))}`)
  );
  candidates.rubrics = [...(source.rubrics || [])]
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((rubric) => {
      const text = htmlToText(rubric.content);
      const tags = [];
      if (!text) {
        tags.push(rubric.generated === false
          ? { label: 'Formulaire à compléter', tone: 'accent' }
          : { label: 'Vide', tone: 'muted' });
      }
      if (rubric.isEditing) tags.push({ label: 'En cours de modification', tone: 'muted' });
      return {
        key: String(rubric.id),
        primary: rubric.title || 'Sans titre',
        excerpt: text.length > 240 ? `${text.slice(0, 240).trim()}…` : text,
        words: text ? text.split(' ').length : 0,
        html: rubric.content || '',
        version: rubric.version || '',
        tags,
        duplicate: existingRubrics.has(`${norm(rubric.title)}|${norm(text)}`),
        searchText: norm(`${rubric.title} ${text}`)
      };
    });

  return candidates;
};

const emptySelection = () => SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: new Set() }), {});

// Par défaut : tout ce qui s'ajoute est coché sauf les doublons ; rien de ce qui remplace n'est coché
const defaultSelection = (candidates) => LIST_KEYS.reduce((acc, key) => ({
  ...acc,
  [key]: new Set(candidates[key].filter((item) => !item.duplicate).map((item) => item.key))
}), emptySelection());

const getErrorMessage = (error, fallback) => {
  if (error.response) return error.response.data?.message || `Erreur ${error.response.status}`;
  if (error.request) return 'Impossible de communiquer avec le serveur. Vérifiez votre connexion.';
  return error.message || fallback;
};

const getAccessReason = (sourceReport, matricule) => {
  if (sourceReport?.access?.type !== 'restricted') return 'public';
  return (sourceReport.access.users || []).includes(matricule) ? 'listed' : 'moderation';
};

const ACCESS_REASON_TOOLTIPS = {
  public: 'PV ouvert à tous les utilisateurs.',
  listed: 'PV restreint : vous faites partie des utilisateurs autorisés.',
  moderation: 'PV restreint : accès accordé par vos droits de modération.'
};

const ACCESS_REASON_LOG_LABELS = {
  public: 'PV ouvert à tous',
  listed: 'PV restreint - utilisateur autorisé',
  moderation: 'PV restreint - droits de modération'
};

function SelectableRow({ checked, onToggle, palette: c, checkboxSx, actions, children }) {
  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <Box
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        pl: 0.5,
        pr: 1,
        py: 0.75,
        cursor: 'pointer',
        borderLeft: `3px solid ${checked ? c.accent : 'transparent'}`,
        backgroundColor: checked ? c.selected : 'transparent',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': { backgroundColor: checked ? c.selected : c.hover },
        '&:focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: '-2px' }
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        readOnly
        tabIndex={-1}
        disableRipple
        sx={{ p: 0.5, ...checkboxSx }}
      />
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.4 }}>
        {children}
      </Box>
      {actions && (
        <Box onClick={(event) => event.stopPropagation()} sx={{ flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

function RepriseDialog({ open, onClose, report, reloadReportData }) {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isDark = theme === 'dark';
  const c = getPalette(isDark);
  const matricule = typeof user === 'object' ? user?.matricule : user;

  const [numberInput, setNumberInput] = useState('');
  const [yearInput, setYearInput] = useState('');
  const [inputErrors, setInputErrors] = useState({});
  const [lookupError, setLookupError] = useState('');
  const [loadingSource, setLoadingSource] = useState(false);
  const [source, setSource] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [selection, setSelection] = useState(emptySelection);
  const [filterText, setFilterText] = useState('');
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [rubricsPlacement, setRubricsPlacement] = useState('end');
  const [expandedRubrics, setExpandedRubrics] = useState(() => new Set());
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const yearInputRef = useRef(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!open) return;
    setNumberInput('');
    setYearInput('');
    setInputErrors({});
    setLookupError('');
    setSource(null);
    setSelection(emptySelection());
    setFilterText('');
    setHideDuplicates(false);
    setRubricsPlacement('end');
    setExpandedRubrics(new Set());
    setCollapsedSections(new Set());
    setSubmitError('');
  }, [open]);

  // Raccourcis vers les derniers PV consultés par l'utilisateur (hors PV ouvert et PV archivés)
  useEffect(() => {
    if (!open || !matricule) return undefined;
    let cancelled = false;
    const token = sessionStorage.getItem('authToken');
    axios.get(`/api/user/${matricule}/accessed-reports`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(({ data }) => {
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : [])
          .filter((item) => !item.archived && !(item.number === report?.number && item.year === report?.year))
          .sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed))
          .slice(0, 8);
        setRecentReports(list);
      })
      .catch(() => {
        if (!cancelled) setRecentReports([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, matricule, report?.number, report?.year]);

  const candidates = useMemo(() => (source ? buildCandidates(source, report) : null), [source, report]);
  const query = norm(filterText);

  const visibleItems = useMemo(() => {
    if (!candidates) return {};
    return SECTION_KEYS.reduce((acc, key) => ({
      ...acc,
      [key]: candidates[key].filter((item) =>
        (!hideDuplicates || !(item.duplicate || item.identical)) &&
        (!query || item.searchText.includes(query))
      )
    }), {});
  }, [candidates, hideDuplicates, query]);

  const totals = useMemo(() => {
    const generalCount = selection.general.size;
    const elementsCount = ELEMENT_KEYS.reduce((sum, key) => sum + selection[key].size, 0);
    const rubricsCount = selection.rubrics.size;
    return { generalCount, elementsCount, rubricsCount, all: generalCount + elementsCount + rubricsCount };
  }, [selection]);

  const duplicatesCount = candidates
    ? LIST_KEYS.reduce((sum, key) => sum + candidates[key].filter((item) => item.duplicate).length, 0)
    : 0;
  const sourceElementsCount = candidates
    ? ELEMENT_KEYS.reduce((sum, key) => sum + candidates[key].length, 0)
    : 0;
  const sourceIsEmpty = candidates && SECTION_KEYS.every((key) => candidates[key].length === 0);
  const nothingVisible = candidates && !sourceIsEmpty && SECTION_KEYS.every((key) => visibleItems[key].length === 0);

  const loadSource = async (rawNumber, rawYear) => {
    const number = normalizeReportNumber(rawNumber);
    const year = normalizeReportYear(rawYear);
    const errors = {};
    if (!rawNumber) errors.number = 'Le numéro est obligatoire';
    else if (!number) errors.number = 'Numéro invalide (ex. 123 ou 000123)';
    if (!rawYear) errors.year = 'L\'année est obligatoire';
    else if (!year) errors.year = '2 ou 4 chiffres (ex. 26)';
    setInputErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setNumberInput(number);
    setYearInput(year);
    setSubmitError('');

    if (number === report?.number && year === report?.year) {
      setLookupError('Il s\'agit du PV actuellement ouvert : choisissez un autre PV.');
      return;
    }

    setLoadingSource(true);
    setLookupError('');
    try {
      const token = sessionStorage.getItem('authToken');
      const { data } = await axios.get('/api/report/search', {
        params: { number, year },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSource(data);

      // Le contenu du PV source devient lisible sans l'ouvrir : la consultation est tracée sur ce PV
      const consultedLabel = formatReportNumber(data.number, data.year);
      const openedLabel = formatReportNumber(report?.number, report?.year);
      logAction(
        `${LOG_ACTIONS.ELEMENTS_REPRISE_VIEW} : PV ${consultedLabel} consulté depuis le PV ${openedLabel}`,
        consultedLabel,
        {
          description: `Le contenu du PV ${consultedLabel} a été affiché dans l'outil de reprise, lancé depuis le PV ${openedLabel} ouvert par l'utilisateur.`,
          pvConsulte: consultedLabel,
          pvOuvert: openedLabel,
          indicePvConsulte: data.indice || null,
          accesAuPvConsulte: ACCESS_REASON_LOG_LABELS[getAccessReason(data, matricule)],
          elementsAffichesDuPvConsulte: {
            ...ELEMENT_KEYS.reduce((acc, key) => ({ ...acc, [key]: (data[key] || []).length }), {}),
            rubriques: (data.rubrics || []).length
          }
        }
      );

      setSelection(defaultSelection(buildCandidates(data, report)));
      setFilterText('');
      setExpandedRubrics(new Set());
      setCollapsedSections(new Set());
    } catch (error) {
      setSource(null);
      setSelection(emptySelection());
      const label = formatReportNumber(number, year);
      if (error.response?.status === 404) {
        setLookupError(`Aucun PV ${label} n'a été trouvé.`);
      } else if (error.response?.status === 403) {
        setLookupError(`Vous n'avez pas accès au PV ${label} : il est restreint et vous ne faites pas partie des utilisateurs autorisés.`);
      } else {
        setLookupError(getErrorMessage(error, 'Impossible de charger le PV source.'));
      }
    } finally {
      setLoadingSource(false);
    }
  };

  const handleNumberChange = (event) => {
    const { number, year } = splitReportInput(event.target.value);
    if (number.length > 6) return;
    setNumberInput(number);

    // Un "/" tapé (ou collé, ex. "123/26") renvoie vers le champ année
    if (year !== null) {
      if (year) setYearInput(year);
      yearInputRef.current?.focus();
    }
  };

  const toggleItem = (sectionKey, itemKey) => {
    setSelection((prev) => {
      const next = new Set(prev[sectionKey]);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return { ...prev, [sectionKey]: next };
    });
  };

  const getSectionState = (sectionKey) => {
    const items = visibleItems[sectionKey] || [];
    const selectedVisible = items.filter((item) => selection[sectionKey].has(item.key)).length;
    return {
      checked: items.length > 0 && selectedVisible === items.length,
      indeterminate: selectedVisible > 0 && selectedVisible < items.length,
      disabled: items.length === 0
    };
  };

  // Les actions groupées ne portent que sur les lignes visibles (filtre et masquage appliqués)
  const toggleSection = (sectionKey) => {
    const items = visibleItems[sectionKey] || [];
    const { checked } = getSectionState(sectionKey);
    setSelection((prev) => {
      const next = new Set(prev[sectionKey]);
      items.forEach((item) => (checked ? next.delete(item.key) : next.add(item.key)));
      return { ...prev, [sectionKey]: next };
    });
  };

  const setAllVisible = (value) => {
    setSelection((prev) => SECTION_KEYS.reduce((acc, key) => {
      const next = new Set(prev[key]);
      (visibleItems[key] || []).forEach((item) => (value ? next.add(item.key) : next.delete(item.key)));
      return { ...acc, [key]: next };
    }, {}));
  };

  const toggleCollapsed = (sectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  const scrollToSection = (sectionKey) => {
    setCollapsedSections((prev) => {
      if (!prev.has(sectionKey)) return prev;
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
    requestAnimationFrame(() => {
      sectionRefs.current[sectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const toggleRubricPreview = (rubricKey) => {
    setExpandedRubrics((prev) => {
      const next = new Set(prev);
      if (next.has(rubricKey)) next.delete(rubricKey);
      else next.add(rubricKey);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!report?._id || !source || totals.all === 0) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const token = sessionStorage.getItem('authToken');
      const payload = {
        source: { number: source.number, year: source.year },
        general: [...selection.general],
        elements: ELEMENT_KEYS.reduce((acc, key) => ({ ...acc, [key]: [...selection[key]] }), {}),
        rubrics: [...selection.rubrics],
        rubricsPlacement
      };
      const { data } = await axios.post(`/api/report/${report._id}/reprise`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      const summary = data?.summary || { general: [], elements: {}, rubrics: 0 };
      const sourceLabel = formatReportNumber(source.number, source.year);
      const elementsCount = Object.values(summary.elements || {}).reduce((sum, count) => sum + count, 0);

      const loggedParts = [
        ...(summary.general || []).map((key) => GENERAL_LOG_LABELS[key] || key),
        ...SECTIONS
          .filter(({ key }) => summary.elements?.[key])
          .map(({ key, label }) => `${label.toLowerCase()} (${summary.elements[key]})`),
        ...(summary.rubrics ? [`rubriques (${summary.rubrics})`] : [])
      ];
      logAction(LOG_ACTIONS.ELEMENTS_REPRISE, formatReportNumber(report.number, report.year), {
        sourceReport: sourceLabel,
        elementsRepris: loggedParts.join(', '),
        informationsGenerales: summary.general || [],
        elements: summary.elements || {},
        rubriques: summary.rubrics || 0,
        placementRubriques: summary.rubrics ? rubricsPlacement : null
      });

      await reloadReportData?.();

      const messageParts = [
        elementsCount && plural(elementsCount, 'élément ajouté', 'éléments ajoutés'),
        summary.rubrics && plural(summary.rubrics, 'rubrique ajoutée', 'rubriques ajoutées'),
        summary.general?.length && plural(summary.general.length, 'information remplacée', 'informations remplacées')
      ].filter(Boolean);
      setSnackbar(`Reprise depuis le PV ${sourceLabel} : ${messageParts.join(', ')}.`);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la reprise des éléments:', error);
      setSubmitError(getErrorMessage(error, 'Une erreur est survenue lors de la reprise.'));
    } finally {
      setSubmitting(false);
    }
  };

  const sectionTitleSx = {
    color: c.title,
    fontWeight: isDark ? 600 : 700,
    textTransform: isDark ? 'none' : 'uppercase',
    letterSpacing: isDark ? 'normal' : '0.03em',
    fontSize: isDark ? '0.875rem' : '0.72rem'
  };

  const checkboxSx = {
    color: c.textSecondary,
    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: c.accent }
  };

  const inputSx = {
    '& .MuiInputBase-root': {
      backgroundColor: isDark ? '#1a252f' : '#fff',
      color: c.text
    },
    '& .MuiOutlinedInput-root:not(.Mui-error)': {
      '& fieldset': { borderColor: isDark ? c.border : undefined },
      '&:hover fieldset': { borderColor: isDark ? c.accent : undefined },
      '&.Mui-focused fieldset': { borderColor: c.accent }
    },
    '& .MuiInputLabel-root:not(.Mui-error)': {
      color: c.label,
      '&.Mui-focused': { color: c.accent }
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.textSecondary }
  };

  const alertSx = (severity) => (isDark ? {
    backgroundColor: severity === 'error' ? c.dangerSoft : c.successSoft,
    color: severity === 'error' ? c.danger : c.success,
    '& .MuiAlert-icon': { color: severity === 'error' ? c.danger : c.success }
  } : {});

  const toneColors = {
    accent: [c.accentSoft, c.accent],
    warning: [c.warningSoft, c.warning],
    success: [c.successSoft, c.success],
    muted: [c.mutedSoft, c.textSecondary]
  };

  const renderTag = ({ label, tone = 'accent', icon }) => (
    <Chip
      key={label}
      label={label}
      icon={icon}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: toneColors[tone][0],
        color: toneColors[tone][1],
        '& .MuiChip-label': { px: 0.75 },
        '& .MuiChip-icon': { color: toneColors[tone][1], fontSize: 13, ml: 0.75, mr: -0.25 }
      }}
    />
  );

  const renderDuplicateTag = () => (
    <Tooltip title="Un élément identique semble déjà exister dans le PV ouvert" key="duplicate">
      {renderTag({ label: 'Déjà présent', tone: 'warning' })}
    </Tooltip>
  );

  const renderElementRow = (sectionKey, item) => (
    <SelectableRow
      checked={selection[sectionKey].has(item.key)}
      onToggle={() => toggleItem(sectionKey, item.key)}
      palette={c}
      checkboxSx={checkboxSx}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: c.text }}>
          {item.primary}
        </Typography>
        {item.tags.map(renderTag)}
        {item.duplicate && renderDuplicateTag()}
      </Box>
      {item.secondary.map((line, index) => (
        <Typography key={`${index}-${line}`} variant="body2" sx={{ color: c.textSecondary, fontSize: '0.8rem', lineHeight: 1.45 }}>
          {line}
        </Typography>
      ))}
    </SelectableRow>
  );

  const renderGeneralRow = (item) => {
    const checked = selection.general.has(item.key);
    return (
      <SelectableRow
        checked={checked}
        onToggle={() => toggleItem('general', item.key)}
        palette={c}
        checkboxSx={checkboxSx}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: c.text }}>
            {item.label}
          </Typography>
          {item.identical
            ? renderTag({ label: 'Identique au PV ouvert', tone: 'success' })
            : checked && renderTag({ label: 'Remplacera la valeur actuelle', tone: 'warning' })}
        </Box>
        <Typography variant="body2" sx={{ color: c.text, fontSize: '0.85rem' }}>
          {item.value}
        </Typography>
        {!item.identical && (
          <Typography variant="body2" sx={{ color: c.textSecondary, fontSize: '0.8rem' }}>
            Actuellement : {item.current}
          </Typography>
        )}
      </SelectableRow>
    );
  };

  const richTextSx = {
    color: c.text,
    fontSize: '0.9rem',
    textAlign: 'justify',
    '& h1, & h2, & h3': { margin: '0.5em 0', fontWeight: 'bold', textAlign: 'left', color: c.text },
    '& h1': { fontSize: '1.3em' },
    '& h2': { fontSize: '1.15em' },
    '& h3': { fontSize: '1.05em' },
    '& p': { margin: '0.4em 0' },
    '& ul, & ol': { paddingLeft: '2em', margin: '0.4em 0' },
    '& blockquote': {
      borderLeft: isDark ? '3px solid #4fc3f7' : '3px solid #ccc',
      paddingLeft: '1em',
      margin: '0.5em 0',
      fontStyle: 'italic',
      color: isDark ? '#bdc3c7' : 'inherit'
    },
    '& img': { maxWidth: '100%' },
    '& table': { maxWidth: '100%', borderCollapse: 'collapse', textAlign: 'left' },
    '& td, & th': { verticalAlign: 'top', textAlign: 'left' },
    '& a': { color: c.accent }
  };

  const renderRubricRow = (item) => {
    const expanded = expandedRubrics.has(item.key);
    return (
      <>
        <SelectableRow
          checked={selection.rubrics.has(item.key)}
          onToggle={() => toggleItem('rubrics', item.key)}
          palette={c}
          checkboxSx={checkboxSx}
          actions={item.html ? (
            <Tooltip title={expanded ? 'Masquer l\'aperçu' : 'Aperçu du contenu'}>
              <IconButton
                size="small"
                onClick={() => toggleRubricPreview(item.key)}
                sx={{ color: c.accent, '&:hover': { backgroundColor: c.accentSoft } }}
              >
                {expanded ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          ) : null}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: c.text }}>
              {item.primary}
            </Typography>
            {item.tags.map(renderTag)}
            {item.duplicate && renderDuplicateTag()}
          </Box>
          {item.excerpt && !expanded && (
            <Typography
              variant="body2"
              sx={{
                color: c.textSecondary,
                fontSize: '0.8rem',
                lineHeight: 1.45,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {item.excerpt}
            </Typography>
          )}
          {item.words > 0 && (
            <Typography variant="caption" sx={{ color: c.textSecondary, opacity: 0.85 }}>
              {plural(item.words, 'mot')}
            </Typography>
          )}
        </SelectableRow>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              ml: 6,
              mr: 1.5,
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: c.previewBg,
              border: `1px solid ${c.border}`,
              maxHeight: 320,
              overflowY: 'auto',
              ...richTextSx
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: normalizeRubricHtml(item.html, { version: item.version }) }} />
          </Box>
        </Collapse>
      </>
    );
  };

  const renderSectionExtra = (sectionKey) => {
    if (sectionKey !== 'rubrics') return null;
    return (
      <ToggleButtonGroup
        exclusive
        size="small"
        value={rubricsPlacement}
        onChange={(_, value) => value && setRubricsPlacement(value)}
        disabled={selection.rubrics.size === 0}
        sx={{
          '& .MuiToggleButton-root': {
            py: 0.25,
            px: 1,
            gap: 0.5,
            fontSize: '0.72rem',
            textTransform: 'none',
            color: c.textSecondary,
            borderColor: c.border,
            '&.Mui-selected': {
              color: c.accent,
              backgroundColor: c.accentSoft,
              '&:hover': { backgroundColor: c.accentSoft }
            }
          }
        }}
      >
        <ToggleButton value="start">
          <VerticalAlignTopIcon sx={{ fontSize: 16 }} />
          Au début du PV
        </ToggleButton>
        <ToggleButton value="end">
          <VerticalAlignBottomIcon sx={{ fontSize: 16 }} />
          À la fin du PV
        </ToggleButton>
      </ToggleButtonGroup>
    );
  };

  const renderSection = ({ key, label, icon: Icon, description }) => {
    const items = visibleItems[key] || [];
    const total = candidates[key].length;
    if (total === 0 || items.length === 0) return null;
    const collapsed = collapsedSections.has(key);
    const state = getSectionState(key);

    return (
      <Box key={key} ref={(el) => { sectionRefs.current[key] = el; }} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: c.dialogBg,
            pt: 1.25,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5
          }}
        >
          <IconButton
            size="small"
            onClick={() => toggleCollapsed(key)}
            sx={{
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              color: isDark ? c.accent : '#2c3e50'
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Checkbox
            size="small"
            checked={state.checked}
            indeterminate={state.indeterminate}
            onChange={() => toggleSection(key)}
            sx={{ p: 0.5, ...checkboxSx }}
          />
          <Icon sx={{ fontSize: 18, color: c.accent, mx: 0.5 }} />
          <Typography variant="body2" sx={sectionTitleSx}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: c.textSecondary, ml: 0.5, fontWeight: 500 }}>
            ({selection[key].size}/{total})
          </Typography>
          <Typography variant="caption" sx={{ color: c.textSecondary, ml: 1, display: { xs: 'none', sm: 'block' } }}>
            {description}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {renderSectionExtra(key)}
        </Box>
        <Collapse in={!collapsed} timeout="auto">
          <Paper
            elevation={0}
            sx={{
              backgroundColor: c.surface,
              backgroundImage: 'none',
              border: isDark ? 'none' : `1px solid ${c.border}`,
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            {items.map((item, index) => (
              <Box key={item.key}>
                {index > 0 && <Divider sx={{ borderColor: c.border }} />}
                {key === 'general' && renderGeneralRow(item)}
                {key === 'rubrics' && renderRubricRow(item)}
                {key !== 'general' && key !== 'rubrics' && renderElementRow(key, item)}
              </Box>
            ))}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  const renderSidebar = () => (
    <Box
      sx={{
        width: 260,
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        borderRight: `1px solid ${c.border}`,
        backgroundColor: c.surface,
        overflowY: 'auto'
      }}
    >
      <Typography variant="body2" sx={{ ...sectionTitleSx, px: 2, pt: 1.75, pb: 1 }}>
        Contenu du PV source
      </Typography>
      {SECTIONS.map(({ key, label, icon: Icon }) => {
        const total = candidates[key].length;
        const selected = selection[key].size;
        const state = getSectionState(key);
        return (
          <Box
            key={key}
            onClick={() => total > 0 && scrollToSection(key)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mx: 1,
              pr: 1,
              borderRadius: 1,
              cursor: total > 0 ? 'pointer' : 'default',
              opacity: total > 0 ? 1 : 0.45,
              '&:hover': total > 0 ? { backgroundColor: c.hover } : undefined
            }}
          >
            <Checkbox
              size="small"
              checked={state.checked}
              indeterminate={state.indeterminate}
              disabled={state.disabled}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleSection(key)}
              sx={{ p: 0.75, ...checkboxSx }}
            />
            <Icon sx={{ fontSize: 18, color: total > 0 ? c.accent : c.textSecondary, mr: 1 }} />
            <Typography variant="body2" noWrap sx={{ flex: 1, color: c.text, fontSize: '0.85rem' }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ color: selected > 0 ? c.accent : c.textSecondary, fontWeight: 600 }}>
              {selected}/{total}
            </Typography>
          </Box>
        );
      })}
      <Divider sx={{ borderColor: c.border, my: 1.5, mx: 2 }} />
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="body2" sx={{ ...sectionTitleSx, mb: 1 }}>
          Sélection
        </Typography>
        {totals.all === 0 ? (
          <Typography variant="body2" sx={{ color: c.textSecondary, fontStyle: 'italic', fontSize: '0.8rem' }}>
            Rien n'est sélectionné.
          </Typography>
        ) : (
          <>
            {totals.elementsCount > 0 && (
              <Typography variant="body2" sx={{ color: c.text, fontSize: '0.8rem' }}>
                {plural(totals.elementsCount, 'élément ajouté', 'éléments ajoutés')}
              </Typography>
            )}
            {totals.rubricsCount > 0 && (
              <Typography variant="body2" sx={{ color: c.text, fontSize: '0.8rem' }}>
                {plural(totals.rubricsCount, 'rubrique ajoutée', 'rubriques ajoutées')} {rubricsPlacement === 'start' ? 'au début' : 'à la fin'}
              </Typography>
            )}
            {totals.generalCount > 0 && (
              <Typography variant="body2" sx={{ color: c.warning, fontSize: '0.8rem' }}>
                {plural(totals.generalCount, 'information remplacée', 'informations remplacées')}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const renderEmptyState = () => (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, overflowY: 'auto' }}>
      {loadingSource ? (
        <CircularProgress sx={{ color: c.accent }} />
      ) : (
        <Box sx={{ maxWidth: 540, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: c.accentSoft,
              color: c.accent
            }}
          >
            <LoupeIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: c.text, mb: 1 }}>
            Choisissez le PV dont vous voulez reprendre le contenu
          </Typography>
          <Typography variant="body2" sx={{ color: c.textSecondary, mb: 2 }}>
            Tous ses éléments et rubriques s'affichent ensuite ici : cochez précisément ce qui doit être repris dans le PV ouvert.
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 1,
              textAlign: 'left',
              backgroundColor: c.surface,
              backgroundImage: 'none',
              border: `1px solid ${c.border}`
            }}
          >
            <InfoOutlinedIcon sx={{ color: c.accent, fontSize: 20, mt: 0.1 }} />
            <Typography variant="body2" sx={{ color: c.textSecondary }}>
              Vous pouvez reprendre depuis tout PV auquel vous avez accès : un PV ouvert à tous, un PV restreint dont vous faites partie des utilisateurs autorisés, ou n'importe quel PV si vous êtes modérateur.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );

  const renderSourceSummary = () => {
    const restricted = source.access?.type === 'restricted';
    const accessTooltip = ACCESS_REASON_TOOLTIPS[getAccessReason(source, matricule)];

    return (
      <Box
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          backgroundColor: c.dialogBg,
          border: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Typography variant="body2" sx={{ color: c.textSecondary }}>
          PV source
        </Typography>
        <Typography sx={{ fontWeight: 700, color: c.text, letterSpacing: '0.02em' }}>
          {formatReportNumber(source.number, source.year)}
        </Typography>
        {renderTag({ label: describeStructure(source) })}
        {source.unit && renderTag({ label: source.unit, tone: 'muted' })}
        <Tooltip title={accessTooltip}>
          {renderTag({
            label: restricted ? 'Accès restreint' : 'Ouvert à tous',
            tone: restricted ? 'warning' : 'success',
            icon: restricted ? <LockIcon /> : <PublicIcon />
          })}
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: c.textSecondary }}>
          {plural(sourceElementsCount, 'élément')} · {plural(candidates.rubrics.length, 'rubrique')}
        </Typography>
      </Box>
    );
  };

  const renderSelectionArea = () => (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
      {renderSidebar()}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: `1px solid ${c.border}`,
            backgroundColor: c.surface
          }}
        >
          <TextField
            size="small"
            placeholder="Filtrer : nom, plaque, fait, titre de rubrique…"
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: filterText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilterText('')} sx={{ color: c.textSecondary }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ flex: 1, minWidth: 220, ...inputSx }}
          />
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={hideDuplicates}
                onChange={(event) => setHideDuplicates(event.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: c.accent },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: c.accent }
                }}
              />
            )}
            label="Masquer ce qui est déjà présent"
            sx={{ mx: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: c.textSecondary } }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAllVisible(true)}
              sx={{ textTransform: 'none', color: c.accent }}
            >
              Tout cocher
            </Button>
            <Button
              size="small"
              startIcon={<RemoveDoneIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAllVisible(false)}
              sx={{ textTransform: 'none', color: c.textSecondary }}
            >
              Tout décocher
            </Button>
          </Box>
        </Box>

        {duplicatesCount > 0 && !hideDuplicates && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              backgroundColor: c.warningSoft,
              color: c.warning,
              borderBottom: `1px solid ${c.border}`
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {duplicatesCount > 1
                ? `${duplicatesCount} éléments semblent déjà présents dans le PV ouvert : ils ne sont pas cochés par défaut.`
                : '1 élément semble déjà présent dans le PV ouvert : il n\'est pas coché par défaut.'}
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}>
          {sourceIsEmpty && (
            <Typography variant="body2" sx={{ color: c.textSecondary, fontStyle: 'italic', textAlign: 'center', mt: 4 }}>
              Ce PV ne contient aucun élément ni aucune rubrique à reprendre.
            </Typography>
          )}
          {nothingVisible && (
            <Typography variant="body2" sx={{ color: c.textSecondary, fontStyle: 'italic', textAlign: 'center', mt: 4 }}>
              Aucun élément ne correspond à votre filtre.
            </Typography>
          )}
          {SECTIONS.map(renderSection)}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        fullWidth
        maxWidth="lg"
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            height: fullScreen ? '100%' : '88vh',
            backgroundColor: c.dialogBg,
            backgroundImage: 'none',
            color: c.text,
            borderRadius: fullScreen ? 0 : '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            backgroundColor: c.surface,
            borderBottom: `1px solid ${c.border}`
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              backgroundColor: c.accentSoft,
              color: c.accent
            }}
          >
            <LoupeIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: c.text, lineHeight: 1.3 }}>
              Reprendre d'un autre document
            </Typography>
            <Typography variant="body2" sx={{ color: c.textSecondary }}>
              Vers le PV {formatReportNumber(report?.number, report?.year)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small" sx={{ color: c.textSecondary }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5, backgroundColor: c.surface, borderBottom: `1px solid ${c.border}` }}>
            <Box
              component="form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                loadSource(numberInput, yearInput);
              }}
              sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}
            >
              <TextField
                label="Numéro du PV"
                placeholder="000123"
                size="small"
                autoFocus
                value={numberInput}
                onChange={handleNumberChange}
                onBlur={() => setNumberInput((value) => normalizeReportNumber(value) ?? value)}
                error={!!inputErrors.number}
                helperText={inputErrors.number}
                disabled={loadingSource || submitting}
                sx={{ width: 180, ...inputSx }}
              />
              <Typography sx={{ pt: 1, color: c.textSecondary, fontWeight: 600 }}>/</Typography>
              <TextField
                label="Année"
                placeholder="2026"
                size="small"
                value={yearInput}
                onChange={(event) => setYearInput(event.target.value.replace(/\D/g, ''))}
                onBlur={() => setYearInput((value) => normalizeReportYear(value) ?? value)}
                inputRef={yearInputRef}
                inputProps={{ maxLength: 4 }}
                error={!!inputErrors.year}
                helperText={inputErrors.year}
                disabled={loadingSource || submitting}
                sx={{ width: 140, ...inputSx }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loadingSource || submitting || !numberInput || !yearInput}
                startIcon={loadingSource ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                sx={{
                  height: 40,
                  backgroundColor: isDark ? '#4fc3f7' : undefined,
                  color: isDark ? '#000' : undefined,
                  '&:hover': { backgroundColor: isDark ? '#29b6f6' : undefined }
                }}
              >
                Afficher le contenu
              </Button>
            </Box>

            {recentReports.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                <HistoryIcon sx={{ fontSize: 16, color: c.textSecondary }} />
                <Typography variant="caption" sx={{ color: c.textSecondary, mr: 0.25 }}>
                  Récemment consultés :
                </Typography>
                {recentReports.map((item) => {
                  const active = source && source.number === item.number && source.year === item.year;
                  return (
                    <Tooltip
                      key={`${item.number}-${item.year}`}
                      title={[item.unit, item.reportType === 'initial' && item.indice ? `indice ${item.indice}` : 'subséquent']
                        .filter(Boolean).join(' · ')}
                    >
                      <Chip
                        size="small"
                        label={formatReportNumber(item.number, item.year)}
                        icon={item.restricted ? <LockIcon /> : undefined}
                        onClick={() => loadSource(item.number, item.year)}
                        disabled={loadingSource || submitting}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                          height: 24,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          color: active ? (isDark ? '#000' : '#fff') : c.accent,
                          backgroundColor: active ? c.accent : 'transparent',
                          borderColor: isDark ? 'rgba(79, 195, 247, 0.4)' : 'rgba(25, 118, 210, 0.35)',
                          '& .MuiChip-icon': { fontSize: 13, color: 'inherit' },
                          '&:hover': { backgroundColor: active ? c.accentHover : c.accentSoft }
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            )}

            {lookupError && (
              <Alert severity="error" sx={{ mt: 1.5, ...alertSx('error') }}>
                {lookupError}
              </Alert>
            )}

            {source && candidates && renderSourceSummary()}
          </Box>

          {source && candidates ? renderSelectionArea() : renderEmptyState()}
        </DialogContent>

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError('')} sx={{ mx: 2.5, mb: 1, ...alertSx('error') }}>
            {submitError}
          </Alert>
        )}

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            backgroundColor: c.surface,
            borderTop: `1px solid ${c.border}`
          }}
        >
          <Typography variant="body2" sx={{ color: c.textSecondary }}>
            {source
              ? (totals.all > 0
                ? [
                  totals.elementsCount && plural(totals.elementsCount, 'élément'),
                  totals.rubricsCount && plural(totals.rubricsCount, 'rubrique'),
                  totals.generalCount && plural(totals.generalCount, 'information générale', 'informations générales')
                ].filter(Boolean).join(' · ')
                : 'Cochez les éléments à reprendre.')
              : 'Aucun PV source sélectionné.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button onClick={onClose} disabled={submitting} sx={{ color: isDark ? '#bdc3c7' : 'text.secondary' }}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!source || totals.all === 0 || submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ContentPasteGoIcon />}
              sx={{
                backgroundColor: isDark ? '#4fc3f7' : undefined,
                color: isDark ? '#000' : undefined,
                '&:hover': { backgroundColor: isDark ? '#29b6f6' : undefined }
              }}
            >
              {submitting ? 'Reprise en cours…' : `Reprendre la sélection${totals.all > 0 ? ` (${totals.all})` : ''}`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar('')} severity="success" sx={{ width: '100%', ...alertSx('success') }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </>
  );
}

export default RepriseDialog;
